import currency from "currency.js";
import { logger } from "../../utils/logger";
import { BigNumber, ethers } from "ethers";
import { Dispatch, SetStateAction } from "react";
import { useSWRConfig } from "swr";
import {
  PerpWithdrawMaxInput,
  perpWithdrawMax,
} from "../../utils/amount/max/withdrawMax";
import { MaxResult, maxUnavailable } from "../../utils/amount/max/types";
import {
  GetAccount200Response,
  GetAccount200ResponseCollateralsInner,
} from "../../codegen-api";
import { getCurrentChainConfig } from "../../config/chains";
import {
  AllCollaterals,
  IsUSDC,
  getAsset,
} from "../../constants/addresses";
import { ONCHAIN_ESTIMATES } from "../../constants/onchainEstimates";
import { ChainIdEnum } from "../../enums/chain";
import { ChainCodeErrorEnum } from "../../enums/rpcErrorCodes";
import { useWithdrawalTransfer } from "../../hooks/api/withdrawTransfer/useWithdrawalTransfer";
import { useUnifiedVault } from "../../hooks/perpetual-dex/useUnifiedVault";
import { useToast } from "../../hooks/toast";
import { ID_TO_CHAINS } from "../../hooks/wallet/useWallet";
import { getCollateralPrecision } from "../../utils/asset/assets";
import {
  floorToDecimalsString,
  isValidDecimalInput,
  normalizeDecimalString,
  toTokenUnits,
  truncateDecimalString,
} from "../../utils/decimalString";
import { scheduleAccountRefresh } from "../../utils/refreshAccount";
import { getDepositTokenInfo } from "../../utils/spotCollateral";
import { ToastEnum, ToastStatusEnum } from "../../utils/toast";
import { supportedSigningChainIds } from "../../utils/wallet/connectors";

type TranslateFn = (...args: any[]) => any;

export function computeCollateralInfo(
  accountData: GetAccount200Response | undefined,
  asset: AllCollaterals
): GetAccount200ResponseCollateralsInner | undefined {
  return (
    accountData?.collaterals?.find((c) => {
      // The API returns collateral_asset as an object ({ asset_id, decimals, ... })
      // even though the generated type is a string enum — match on the id so each
      // asset (USDC/WBTC/WETH) resolves to ITS OWN balance, not the total.
      const ca = c.collateral_asset as unknown;
      const id =
        typeof ca === "string"
          ? ca
          : (ca as { asset_id?: string } | null)?.asset_id;
      return id === getAsset(asset);
    }) ??
    // Fallback ONLY when the API omits the collaterals array entirely but still
    // populates the top-level USDC balance. When collaterals ARE present, never
    // substitute the account total ($equity) for a single asset's balance.
    ((!accountData?.collaterals || accountData.collaterals.length === 0) &&
    IsUSDC(asset) &&
    accountData?.balance != null
      ? ({
          collateral_asset: getAsset(asset) as any,
          balance: accountData.balance,
          available_balance: accountData.available_balance,
          collateral_value: accountData.balance,
          collateral_yield_bearing: false,
          pending_withdrawals: "0",
          unrealized_pnl: "0",
        } as GetAccount200ResponseCollateralsInner)
      : undefined)
  );
}

/** The backend sent `withdrawable: null`: the account cannot be valued right now. */
export const isWithdrawableUnknown = (
  accountData: GetAccount200Response | undefined
): boolean => (accountData?.withdrawable as unknown) === null;

/** Shown when a typed amount is not plain decimal text within the token's decimals. */
export const invalidAmountMessage = (t: TranslateFn, decimals: number) =>
  t("invalid_amount", {
    defaultValue: "Enter a valid amount with at most {{decimals}} decimals",
    decimals,
  });

/**
 * Amount validation. With an unknown withdrawable NO amount is valid — neither a
 * max nor a typed value can be checked against what margin still needs.
 *
 * Amounts are compared in token base units: `parseFloat` let "1.5abc" through
 * and compared 18-decimal figures after rounding them.
 */
export const withdrawAmountRules = (
  withdrawableBalance: string | undefined,
  withdrawableUnknown: boolean,
  decimals: number
) => ({
  withdrawableKnown: () => !withdrawableUnknown,
  validDecimal: (v: string) => isValidDecimalInput(v, decimals),
  moreThanZero: (v: string) =>
    Boolean(toTokenUnits(v, decimals)?.gt(0)),
  lessThanEqualToExchangeBalance: (v: string) => {
    const amount = toTokenUnits(v, decimals);
    const max = toTokenUnits(withdrawableBalance ?? "0", decimals);
    return Boolean(amount && max && amount.lte(max));
  },
});

/** The value Max fills in, or undefined when Max must do nothing. */
export const maxWithdrawValue = (
  withdrawableBalance: string | undefined,
  withdrawableUnknown: boolean
): string | undefined =>
  withdrawableUnknown ? undefined : withdrawableBalance ?? "0";

/**
 * Decimals a copyable figure of `asset` is shown at: the token registry's
 * display decimals (WBTC 6, WETH 4, USDC 2), else the collateral precision.
 */
export const assetDisplayDecimals = (asset: AllCollaterals | string): number =>
  getDepositTokenInfo(getCurrentChainConfig().chainId, asset)?.displayDecimals ??
  getCollateralPrecision(asset as AllCollaterals);

/**
 * A balance / withdrawable label, FLOORED at `decimals` with thousands
 * separators. `currency(x).format()` rounded half-up at 2dp for every asset, so
 * 0.005 WBTC read "0.01" — more than exists.
 */
export const formatFlooredAmount = (
  value: string | number | null | undefined,
  decimals: number
): string =>
  currency(floorToDecimalsString(value, decimals), {
    precision: decimals,
  }).format({ symbol: "" });

const ZERO = BigNumber.from(0);

/**
 * An API decimal string as base units, floored to `decimals` (or CEILED, for a
 * figure that is subtracted, so the result can never exceed what exists). A
 * negative figure is zero; missing or non-numeric text is undefined.
 */
const apiUnits = (
  value: string | number | null | undefined,
  decimals: number,
  roundUp = false
): BigNumber | undefined => {
  if (value === null || value === undefined || value === "") return undefined;
  const plain = normalizeDecimalString(value);
  if (plain === null) return undefined;
  if (plain.startsWith("-")) return ZERO;
  const units = toTokenUnits(plain, decimals);
  if (!units) return undefined;
  return roundUp && truncateDecimalString(plain, decimals) !== plain
    ? units.add(1)
    : units;
};

const minUnits = (a: BigNumber, b: BigNumber) => (a.lt(b) ? a : b);

/**
 * min(balance, available) - credit, in base units and clamped at zero: the
 * collateral figure both withdraw and transfer start from. Balances are floored
 * and credit ceiled to `decimals`, so it never exceeds what the account holds.
 */
export function collateralSpendableUnits(
  accountData: GetAccount200Response | undefined,
  collateralInfo: GetAccount200ResponseCollateralsInner,
  asset: AllCollaterals,
  decimals: number
): BigNumber {
  const balance = apiUnits(collateralInfo.balance, decimals) ?? ZERO;
  const creditBalance =
    IsUSDC(asset) && accountData?.credit
      ? apiUnits(accountData.credit, decimals, true) ?? ZERO
      : ZERO;
  const availableBalance =
    collateralInfo.available_balance != null
      ? apiUnits(collateralInfo.available_balance, decimals) ?? ZERO
      : balance;
  const afterCredit = minUnits(balance, availableBalance).sub(creditBalance);
  return afterCredit.lt(0) ? ZERO : afterCredit;
}

/** Base units as the plain decimal text a Max button fills in ("150", "0.0000005"). */
export const unitsToAmountText = (units: BigNumber, decimals: number): string =>
  normalizeDecimalString(ethers.utils.formatUnits(units, decimals)) ?? "0";

/**
 * Withdrawable amount in base units. Worked in BigNumber: float math rounded an
 * 18-decimal balance UP by a few wei, printed tiny amounts as "5e-7", turned
 * 1.15 - 0.05 into 1.0999999999999999 (parseUnits throws) and went negative
 * when credit exceeded the balance.
 */
export function computeWithdrawableUnits(
  accountData: GetAccount200Response | undefined,
  collateralInfo: GetAccount200ResponseCollateralsInner | undefined,
  asset: AllCollaterals,
  decimals: number
): BigNumber | undefined {
  // `withdrawable: null` = the backend cannot value the account (a position is
  // unpriced), so what margin still needs is unknown. Uncapped collateral would
  // let margin-backing funds out; the max is unknown, not the raw balance.
  if (isWithdrawableUnknown(accountData)) {
    return undefined;
  }
  if (collateralInfo) {
    const collateralWithdrawable = collateralSpendableUnits(
      accountData,
      collateralInfo,
      asset,
      decimals
    );
    // Cap by the account-level, margin-aware withdrawable so margin-backing
    // funds can't be withdrawn. Upper bound only — never raises the max.
    const accountWithdrawable = apiUnits(accountData?.withdrawable, decimals);
    return accountWithdrawable !== undefined
      ? minUnits(collateralWithdrawable, accountWithdrawable)
      : collateralWithdrawable;
  }
  return undefined;
}

/**
 * The resolver's view of this collateral row. Shared by withdraw and transfer
 * so both are capped the same way — the transfer form used to apply no margin
 * cap at all, and could move what a withdrawal of the same size refused.
 */
export function fundsMaxInput(
  accountData: GetAccount200Response | undefined,
  collateralInfo: GetAccount200ResponseCollateralsInner | undefined,
  asset: AllCollaterals,
  decimals: number
): PerpWithdrawMaxInput {
  return {
    collateral: collateralInfo
      ? {
          balance: collateralInfo.balance,
          available_balance: collateralInfo.available_balance,
          collateral_value: collateralInfo.collateral_value,
        }
      : undefined,
    // `undefined` (the field is absent) leaves the figure uncapped; `null` (the
    // account cannot be valued) makes it unknown.
    withdrawableUsd: accountData ? accountData.withdrawable : undefined,
    decimals,
    isUsdStable: IsUSDC(asset),
    credit: accountData?.credit,
    unit: String(asset),
  };
}

/**
 * The most this collateral may leave the exchange as, or the reason it is not
 * known. `withdrawable` is a USD figure: for a stablecoin it is dollars either
 * way, and for WBTC / WETH the resolver converts it into tokens through the
 * row's own `collateral_value` (B2 — the cap used to be compared against token
 * amounts, so it never bound for those assets).
 */
export function withdrawMaxResult(
  accountData: GetAccount200Response | undefined,
  collateralInfo: GetAccount200ResponseCollateralsInner | undefined,
  asset: AllCollaterals,
  decimals: number
): MaxResult {
  if (!collateralInfo) return maxUnavailable("balance_unknown");
  return perpWithdrawMax(fundsMaxInput(accountData, collateralInfo, asset, decimals));
}

/** `computeWithdrawableUnits` as plain decimal text ("150", "0.0000005"). */
export function computeWithdrawableBalance(
  accountData: GetAccount200Response | undefined,
  collateralInfo: GetAccount200ResponseCollateralsInner | undefined,
  asset: AllCollaterals,
  decimals: number
): string | undefined {
  const max = withdrawMaxResult(accountData, collateralInfo, asset, decimals);
  return max.ok ? max.max : undefined;
}

interface IPerformWithdrawParams {
  setDepositWithdrawError: Dispatch<SetStateAction<string | undefined>>;
  setNotEnoughBridgeCapacityError: Dispatch<SetStateAction<string | undefined>>;
  setLoading: Dispatch<SetStateAction<boolean>>;
  isUnifiedVaultChain: boolean;
  amount: string | undefined;
  assetDecimals: number;
  vaultWithdraw: ReturnType<typeof useUnifiedVault>["withdraw"];
  createWithdrawal: ReturnType<typeof useWithdrawalTransfer>["createWithdrawal"];
  mutate: ReturnType<typeof useSWRConfig>["mutate"];
  onHide?: () => void;
  addToast: ReturnType<typeof useToast>["addToast"];
  t: TranslateFn;
  apiError: TranslateFn;
  asset: AllCollaterals;
  chainId: number | undefined;
}

export async function performWithdraw({
  setDepositWithdrawError,
  setNotEnoughBridgeCapacityError,
  setLoading,
  isUnifiedVaultChain,
  amount,
  assetDecimals,
  vaultWithdraw,
  createWithdrawal,
  mutate,
  onHide,
  addToast,
  t,
  apiError,
  asset,
  chainId,
}: IPerformWithdrawParams) {
  setDepositWithdrawError(undefined);
  setNotEnoughBridgeCapacityError(undefined);

  try {
    setLoading(true);

    // Floored to the asset's decimals; `parseUnits` on the typed text threw a
    // raw ethers error for "5e-7" or one decimal too many.
    const rawAmount = toTokenUnits(amount, assetDecimals);
    if (!rawAmount || rawAmount.isZero()) {
      setDepositWithdrawError(invalidAmountMessage(t, assetDecimals));
      return;
    }
    const amountText = ethers.utils.formatUnits(rawAmount, assetDecimals);

    // Unified vault chains (DogeOS, Monad, etc.) withdraw directly via contract
    if (isUnifiedVaultChain) {
      await vaultWithdraw(rawAmount.toString());
      scheduleAccountRefresh(mutate);
      onHide?.();
      addToast(
        {
          type: ToastEnum.SIMPLE,
          header: t("withdrawal_pending"),
          subheader: t("withdrawal_pending_desc", {
            amount: `${currency(amount || "0").format({ symbol: "" })} ${asset}`,
            time: `${ONCHAIN_ESTIMATES.withdrawal.value} ${ONCHAIN_ESTIMATES.withdrawal.unit}`,
          }),
          status: ToastStatusEnum.SUCCESS,
        },
        4000
      );
      return;
    }

    const { response, insufficientLimit } = await createWithdrawal(amountText);
    if (response?.success) {
      scheduleAccountRefresh(mutate);
      // Dismiss modal and show success
      onHide?.();
      addToast(
        {
          type: ToastEnum.SIMPLE,
          header: t("withdrawal_pending"),
          subheader: t("withdrawal_pending_desc", {
            amount: `${currency(amount || "0").format({
              symbol: "",
            })} ${asset}`,
            time: `${ONCHAIN_ESTIMATES.withdrawal.value} ${ONCHAIN_ESTIMATES.withdrawal.unit}`,
          }),
          status: ToastStatusEnum.SUCCESS,
        },
        4000
      );
    } else if (insufficientLimit) {
      const chainEnum = chainId as ChainIdEnum;
      const otherNetworks = supportedSigningChainIds
        .filter((c) => c !== chainEnum)
        .map((c) => ID_TO_CHAINS[c]);
      const otherNetworksText =
        otherNetworks.length === 2
          ? otherNetworks.join(` ${t("or")} `)
          : otherNetworks.join(", ");
      const currentNetworkName = ID_TO_CHAINS[chainEnum];

      // No limit
      if (Number(insufficientLimit) === 0) {
        setNotEnoughBridgeCapacityError(
          t("no_bridge_capacity", {
            currentChain: currentNetworkName,
            otherChains: otherNetworksText,
          })
        );
      } else {
        let text = t("insufficient_bridge_capacity", {
          chain: currentNetworkName,
        });
        text += t("reduce_withdrawal_size", {
          size: `${currency(insufficientLimit).format({
            symbol: "",
          })} ${asset}`,
          otherChains: otherNetworksText,
        });
        setNotEnoughBridgeCapacityError(text);
      }
    }
  } catch (error: any) {
    const errMsg: string = (error?.message || error?.data?.message || "")
      .toString()
      .toLowerCase();
    const userRejected =
      error?.code === ChainCodeErrorEnum.CANCELLED ||
      error?.code === "ACTION_REJECTED" ||
      errMsg.includes("user rejected") ||
      errMsg.includes("user denied") ||
      errMsg.includes("rejected the request");
    // ethers amount-parse failures carry these codes; never show their raw text.
    const amountParseError =
      error?.code === "INVALID_ARGUMENT" || error?.code === "NUMERIC_FAULT";
    if (userRejected) {
      setDepositWithdrawError(t("withdrawal_cancelled"));
    } else if (amountParseError) {
      setDepositWithdrawError(invalidAmountMessage(t, assetDecimals));
      logger.error({ error });
    } else {
      setDepositWithdrawError(
        apiError(error.message) ?? t("withdrawal_failed")
      );
      // eslint-disable-next-line no-console
      logger.error({ error });
    }
  } finally {
    setLoading(false);
  }
}
